import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const UploadDrawingModal = ({ isOpen, onClose, onUpload, sites }) => {
  const { t } = useLanguage();

  const DISCIPLINE_OPTIONS = [
    { value: '', label: t('selectDiscipline') },
    { value: 'architectural', label: t('architectural') },
    { value: 'structural', label: t('structural') },
    { value: 'electrical', label: t('electrical') },
    { value: 'mechanical', label: t('mechanical') },
    { value: 'plumbing', label: t('plumbing') },
    { value: 'civil', label: t('civil') },
    { value: 'fire_protection', label: t('fireProtection') },
    { value: 'landscape', label: t('landscape') },
    { value: 'other', label: t('other') },
  ];

  const [formData, setFormData] = useState({
    drawingName: '',
    drawingNumber: '',
    revision: '',
    siteId: '',
    discipline: '',
    description: '',
    versionNumber: 1
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const siteOptions = [
    { value: '', label: t('selectSite') },
    ...(sites?.map(site => ({
      value: site?.id,
      label: site?.site_name
    })) || [])
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      const maxSize = 50 * 1024 * 1024;

      if (!validTypes?.includes(file?.type)) {
        setErrors(prev => ({ ...prev, file: t('onlyPdfAndImageAllowed') }));
        return;
      }

      if (file?.size > maxSize) {
        setErrors(prev => ({ ...prev, file: t('fileSizeTooLarge') }));
        return;
      }

      setSelectedFile(file);
      setErrors(prev => ({ ...prev, file: '' }));

      if (!formData?.drawingName) {
        const nameWithoutExt = file?.name?.replace(/\.[^/.]+$/, '');
        setFormData(prev => ({ ...prev, drawingName: nameWithoutExt }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.drawingName?.trim()) {
      newErrors.drawingName = t('drawingNameRequired');
    }

    if (!formData?.drawingNumber?.trim()) {
      newErrors.drawingNumber = t('drawingNumberRequired');
    }

    if (!formData?.revision?.trim()) {
      newErrors.revision = t('revisionRequired');
    }

    if (!formData?.siteId) {
      newErrors.siteId = t('siteRequired');
    }

    if (!formData?.discipline) {
      newErrors.discipline = t('disciplineRequired');
    }

    if (!selectedFile) {
      newErrors.file = t('selectFileRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      setUploading(true);
      await onUpload(formData, selectedFile);
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFormData({
        drawingName: '',
        drawingNumber: '',
        revision: '',
        siteId: '',
        discipline: '',
        description: '',
        versionNumber: 1
      });
      setSelectedFile(null);
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="Upload" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('uploadDrawing')}</h2>
              <p className="text-sm text-muted-foreground">{t('uploadDrawingSubtitle')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={uploading}
            aria-label={t('close')}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('selectFile')} <span className="text-error">*</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-accent-foreground hover:file:bg-accent/90 cursor-pointer"
                />
              </div>
              {selectedFile && (
                <p className="text-sm text-muted-foreground mt-2">
                  {t('selected')}: {selectedFile?.name} ({(selectedFile?.size / (1024 * 1024))?.toFixed(2)} MB)
                </p>
              )}
              {errors?.file && (
                <p className="text-sm text-error mt-1">{errors?.file}</p>
              )}
            </div>

            <Input
              label={t('drawingName')}
              type="text"
              placeholder={t('drawingNamePlaceholder')}
              value={formData?.drawingName}
              onChange={(e) => handleChange('drawingName', e?.target?.value)}
              error={errors?.drawingName}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('drawingNumber')}
                type="text"
                placeholder={t('drawingNumberPlaceholder')}
                value={formData?.drawingNumber}
                onChange={(e) => handleChange('drawingNumber', e?.target?.value)}
                error={errors?.drawingNumber}
                required
              />
              <Input
                label={t('revision')}
                type="text"
                placeholder={t('revisionPlaceholder')}
                value={formData?.revision}
                onChange={(e) => handleChange('revision', e?.target?.value)}
                error={errors?.revision}
                required
              />
            </div>

            <Select
              label={t('site')}
              options={siteOptions}
              value={formData?.siteId}
              onChange={(value) => handleChange('siteId', value)}
              error={errors?.siteId}
              required
            />

            <Select
              label={t('discipline')}
              options={DISCIPLINE_OPTIONS}
              value={formData?.discipline}
              onChange={(value) => handleChange('discipline', value)}
              error={errors?.discipline}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('description')}
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder={t('drawingDescriptionPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
              />
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} color="var(--color-accent)" />
                <div>
                  <p className="text-sm text-foreground font-medium mb-1">{t('versionControl')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('versionControlDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            loading={uploading}
            iconName="Upload"
            iconPosition="left"
          >
            {t('uploadDrawing')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadDrawingModal;